import { useState } from "react"
import { CircleAlertIcon, TriangleAlertIcon } from "lucide-react"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { flowTitle } from "./text"

const RULES: { [rule: string]: string } = {
  "1": "Jumps to items that do not exist",
  "2": "Item ids",
  "6": "Unreachable items",
  "6.g": "Options that are never on offer",
  "6.h": "Where a lost conversation goes",
  "7": "Items that say nothing",
  "10": "Items that cannot use the answer",
  "10.b": "Flows that can fall off the end",
  "10.c": "How flows end",
  "10.d": "The start of main",
  "10.e": "How flows end on an ai item",
  "11": "Options without a label",
  "12": "Catch-all matches",
  "12.b": "Duplicate options",
  "12.d": "Matches that repeat the label",
  "14": "Conditions",
  "14.b": "Ai item fields",
  "14.c": "Ai items capturing the message",
  "14.d": "Ai items with options",
  "14.e": "Ways out of ai items",
  "14.f": "Missing model",
  "15": "Vars nothing writes",
  "15.b": "Templates that do not compile",
  "20.b": "Conditions that can never matter",
  "21.b": "Going back without forgetting",
  "classify": "Understanding free text",
  "tools": "Ai tools",
  "lang": "Languages",
  "lang.missing": "Missing translations",
}

function IssueLine({ issue, where, onClick }: { issue: PlaybookIssue, where?: string, onClick?: () => void }) {
  const Icon = issue.level === "error" ? CircleAlertIcon : TriangleAlertIcon
  return <div onClick={onClick} className={cn("flex items-start gap-2 text-sm text-foreground", onClick && "cursor-pointer hover:underline")}>
    <Icon className={cn("size-4 shrink-0 mt-0.5", issue.level === "error" ? "text-red-600" : "text-amber-600")} />
    <span>{where && <span className="font-medium">{where}: </span>}{issue.message} <span className="text-muted-foreground">({issue.rule})</span></span>
  </div>
}

export function ItemIssuesButton({ issues }: { issues: PlaybookIssue[] }) {
  const [open, setOpen] = useState(false)
  if (!issues.length) return null
  const errors = issues.some(i => i.level === "error")
  const Icon = errors ? CircleAlertIcon : TriangleAlertIcon

  return <>
    <Button variant="ghost" size="icon-sm" aria-label="Item issues" title={`${issues.length} ${issues.length === 1 ? "issue" : "issues"} in this item`} onClick={() => setOpen(true)}
      className={errors ? "text-red-600" : "text-amber-600"}>
      <Icon className="size-5" />
    </Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Issues of this item</DialogTitle>
          <DialogDescription>Errors would block a save. Warnings are worth a look</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {issues.map((issue, i) => <IssueLine key={i} issue={issue} />)}
        </div>
      </DialogContent>
    </Dialog>
  </>
}

interface IssuesButtonProps {
  issues: PlaybookIssue[]
  flows: Playbook["flows"]
  onSelectFlow: (flow: string) => void
  onOpenSettings: () => void
}

export function IssuesButton({ issues, flows, onSelectFlow, onOpenSettings }: IssuesButtonProps) {
  const [open, setOpen] = useState(false)
  const errors = issues.filter(i => i.level === "error").length
  const warnings = issues.length - errors
  if (!issues.length) return null

  function go(issue: PlaybookIssue) {
    setOpen(false)
    if (issue.flow) onSelectFlow(issue.flow)
    else onOpenSettings()
  }

  function section(title: string, list: PlaybookIssue[]) {
    const groups = list.reduce((map, i) => map.set(`${i.level} ${i.rule}`, [...map.get(`${i.level} ${i.rule}`) || [], i]), new Map<string, PlaybookIssue[]>())
    if (!list.length) return null
    return <div className="flex flex-col gap-1">
      <h3 className="text-base font-semibold">{title}</h3>
      <Accordion multiple>
        {[...groups].map(([key, group]) => {
          const Icon = group[0].level === "error" ? CircleAlertIcon : TriangleAlertIcon
          return <AccordionItem key={key} value={key}>
            <AccordionTrigger>
              <span className="flex items-center gap-2">
                <Icon className={cn("size-4", group[0].level === "error" ? "text-red-600" : "text-amber-600")} />
                {RULES[group[0].rule] ?? group[0].rule} <span className="text-muted-foreground">({group.length})</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-2">
              {group.map((issue, i) => <IssueLine key={i} issue={issue} onClick={() => go(issue)}
                where={issue.flow ? `${flowTitle(flows, issue.flow)} · ${issue.item || `item ${issue.index! + 1}`}` : undefined} />)}
            </AccordionContent>
          </AccordionItem>
        })}
      </Accordion>
    </div>
  }

  return <>
    <Button variant="ghost" size="sm" onClick={() => setOpen(true)} className="gap-2 text-xs">
      {!!errors && <span className="flex items-center gap-1 text-red-600"><CircleAlertIcon className="size-3.5" />{errors}</span>}
      {!!warnings && <span className="flex items-center gap-1 text-amber-600"><TriangleAlertIcon className="size-3.5" />{warnings}</span>}
    </Button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Validation</DialogTitle>
          <DialogDescription>Errors would block a save. Warnings are worth a look</DialogDescription>
        </DialogHeader>
        {section("Playbook Warnings", issues.filter(i => !i.flow))}
        {section("Item Warnings", issues.filter(i => i.flow))}
      </DialogContent>
    </Dialog>
  </>
}
